import { HttpClient } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import type { IApiResponse } from 'app/common/interfaces';
import { AuthService } from 'app/core/auth/auth.service';
import { TenantContextService } from 'app/core/services/tenant-context.util';
import { environment } from 'environments/environment';
import type { Observable } from 'rxjs';
import { of } from 'rxjs';
import { catchError, map, shareReplay, switchMap, tap } from 'rxjs/operators';
import type { OptionInput } from '../../patient-surveys.interface';
import { CategoryCode, SURVEY_TYPE_LABELS } from '../../shared/constants/constants';
import { buildPatientSurveyJointLateralityParams } from '../../shared/constants/patient-survey-joint-laterality-params';
import {
  type AutomatedSurvey,
  type AutomatedSurveyApiDetail,
  type AutomatedSurveyDetail,
  type AutomatedSurveyOptions,
  type CreateAutomatedSurveyRequest,
  mapApiDetailToAutomatedSurveyDetail,
  type ProcedureType,
  type UpdateAutomatedSurveyRequest,
} from '../automated.interface';

@Injectable({
  providedIn: 'root',
})
export class AutomatedSurveyService {
  private readonly http = inject(HttpClient);
  private readonly schedulerApiUrl = environment.api.microservices.scheduler;
  private readonly patientApiUrl = environment.api.microservices.patient;
  private readonly tenantContextService = inject(TenantContextService);
  private readonly authService = inject(AuthService);

  private readonly appointmentTypesSignal = signal<OptionInput[]>([]);

  private categoryRowsCache: unknown[] = [];

  private readonly jointTypesSignal = signal<OptionInput[]>([]);

  private readonly statusesSignal = signal<OptionInput[]>([]);

  private readonly scheduleModesSignal = signal<OptionInput[]>([]);

  private readonly deliveryMethodsSignal = signal<OptionInput[]>([]);

  private readonly surveyTypesSignal = signal<OptionInput[]>([]);

  private optionsCache$: Observable<AutomatedSurveyOptions> | null = null;

  private getAutomatedSurveyApiUrl(): string | null {
    const tenantId = this.tenantContextService.getTenantId().trim();
    if (!tenantId) {
      if (environment.production) {
        throw new Error('Tenant ID is required but not available');
      }
      return null;
    }
    return `${this.schedulerApiUrl}/v1/tenant/${tenantId}/automated-survey`;
  }

  private getAutomatedSurveyApiUrlOrThrow(): string | null {
    const url = this.getAutomatedSurveyApiUrl();
    if (!url && environment.production) {
      throw new Error('Tenant ID is required but not available');
    }
    return url;
  }

  private getAllowedCategoryCodes(surveyType: string | null | undefined): readonly string[] {
    // Can be removed when backend is updated to return the correct category codes for each survey type
    if (surveyType === SURVEY_TYPE_LABELS.FOLLOWUP_APPOINTMENT_TRIGGERED) {
      return [CategoryCode.FOLLOW_UP];
    }
    return [CategoryCode.NEW_PATIENT, CategoryCode.FOLLOW_UP, CategoryCode.FOLLOW_UP_NON_OP];
  }

  private mapCategories(categoryRows: unknown[], surveyTypeLabel?: string | null): OptionInput[] {
    interface RawCategory {
      id?: string;
      code?: string;
      name?: string;
      sortOrder?: number;
      sort_order?: number;
      isActive?: boolean | number;
      is_active?: boolean | number;
    }
    interface NormalizedCategory {
      id: string;
      code: string;
      name: string;
      isActive: boolean;
    }
    const rows = categoryRows as RawCategory[];
    const normalizedRows: NormalizedCategory[] = rows.map((item) => {
      const activeValue = item.isActive ?? item.is_active;
      const isActive = activeValue === undefined || activeValue === null || activeValue === true || activeValue === 1;
      return {
        id: item.id ?? '',
        code: item.code ?? '',
        name: item.name ?? '',
        isActive,
      };
    });
    const allowedCodes = this.getAllowedCategoryCodes(surveyTypeLabel);
    return normalizedRows
      .filter((item) => {
        const isAllowedCode = allowedCodes.includes(item.code);
        return isAllowedCode && item.isActive;
      })
      .sort((first, second) => first.name.localeCompare(second.name, undefined, { sensitivity: 'base' }))
      .map((item) => ({
        value: item.id || item.code,
        label: item.name,
        type: 'checkbox' as const,
      }));
  }

  public addAutomatedSurvey(request: CreateAutomatedSurveyRequest): Observable<AutomatedSurvey> {
    const url = `${this.getAutomatedSurveyApiUrlOrThrow()}`;
    return this.http.post<AutomatedSurvey>(url, request);
  }

  public updateAutomatedSurvey(surveyId: string, request: UpdateAutomatedSurveyRequest): Observable<AutomatedSurvey> {
    const url = `${this.getAutomatedSurveyApiUrlOrThrow()}/${surveyId}`;
    return this.http.put<AutomatedSurvey>(url, request);
  }

  /**
   * PATCH /scheduler-svc/api/v1/tenant/{tenantId}/automated-survey/{automatedSurveyId}/deactivate
   * Soft-deletes (deactivates) the automated survey by setting isActive to false.
   * Sends user email for audit (updatedBy).
   */
  public deleteAutomatedSurvey(surveyId: string): Observable<void> {
    const url = `${this.getAutomatedSurveyApiUrlOrThrow()}/${surveyId}/deactivate`;
    const email = this.authService.getSignedInUserEmail();
    return this.http.patch<void>(url, { email });
  }

  /**
   * GET /scheduler-svc/api/v1/tenant/{tenantId}/automated-survey/{automatedSurveyId}
   * Loads a single automated survey for the current tenant (used when opening edit view).
   * Backend may return { data: { automatedSurvey: detail } }, { data: detail }, or detail with typeId +
   * appointmentTriggered/anniversaryTriggered; we normalize to AutomatedSurveyDetail.
   */
  public getAutomatedSurveyById(automatedSurveyId: string): Observable<AutomatedSurveyDetail> {
    const url = `${this.getAutomatedSurveyApiUrl()}/${automatedSurveyId}`;
    return this.http
      .get<
        IApiResponse<
          | AutomatedSurveyDetail
          | { automatedSurvey: AutomatedSurveyDetail | AutomatedSurveyApiDetail }
          | AutomatedSurveyApiDetail
        >
      >(url)
      .pipe(
        map((response) => {
          if (response.data == null) {
            throw new Error('Automated survey response missing data');
          }
          const raw = response.data as
            | { automatedSurvey?: AutomatedSurveyDetail | AutomatedSurveyApiDetail }
            | AutomatedSurveyDetail
            | AutomatedSurveyApiDetail;
          const payload =
            'automatedSurvey' in raw && raw.automatedSurvey != null
              ? raw.automatedSurvey
              : (raw as AutomatedSurveyDetail | AutomatedSurveyApiDetail);
          const hasApiShape =
            'typeId' in payload &&
            payload.typeId != null &&
            (('appointmentTriggered' in payload && payload.appointmentTriggered != null) ||
              ('anniversaryTriggered' in payload && payload.anniversaryTriggered != null));
          if (hasApiShape) {
            return mapApiDetailToAutomatedSurveyDetail(payload as AutomatedSurveyApiDetail);
          }
          return payload as unknown as AutomatedSurveyDetail;
        })
      );
  }

  public getAutomatedSurveys(): Observable<AutomatedSurvey[]> {
    return this.http.get<AutomatedSurvey[]>(`${this.schedulerApiUrl}/automated-surveys`);
  }
  public getProcedureTypes(): Observable<ProcedureType[]> {
    return of<ProcedureType[]>([
      { id: 'uuid5', name: 'Hip Arthroscopy', description: 'Hip Arthroscopy' },
      { id: 'uuid6', name: 'Knee Arthroscopy', description: 'Knee Arthroscopy' },
      { id: 'uuid7', name: 'Shoulder Surgery', description: 'Shoulder Surgery' },
      { id: 'uuid8', name: 'Ankle Surgery', description: 'Ankle Surgery' },
    ]);
  }

  public getAppointmentTypeOptions(surveyTypeId: string): Observable<OptionInput[]> {
    const baseUrl = this.getAutomatedSurveyApiUrl();
    if (!baseUrl) {
      return of([]);
    }
    const url = `${baseUrl}/appointment-type-options?surveyTypeId=${surveyTypeId}`;
    return this.http.get<IApiResponse<{ appointmentTypes: { id: string; code: string; name: string }[] }>>(url).pipe(
      map((response) => {
        const appointmentTypes = response.data.appointmentTypes.map((item) => ({
          value: item.id,
          label: item.name,
          type: 'checkbox' as const,
        }));
        this.appointmentTypesSignal.set(appointmentTypes);
        return appointmentTypes;
      })
    );
  }

  private static emptyOptions(): AutomatedSurveyOptions {
    return {
      categories: [],
      jointTypes: [],
      methods: [],
      statuses: [],
      types: [],
    };
  }

  private loadOptions(): Observable<AutomatedSurveyOptions> {
    if (!this.optionsCache$) {
      const url = this.getAutomatedSurveyApiUrl();
      if (!url) {
        return of(AutomatedSurveyService.emptyOptions());
      }
      this.optionsCache$ = this.http.get<IApiResponse<AutomatedSurveyOptions>>(url).pipe(
        switchMap((response) => this.withPatientSurveyJointTypes(this.normalizeSchedulerOptionsPayload(response.data))),
        tap((data) => this.applyLoadedOptionsToSignals(data)),
        catchError((err) => {
          console.error('AutomatedSurveyService.loadOptions failed:', err);
          this.optionsCache$ = null;
          return of(AutomatedSurveyService.emptyOptions());
        }),
        shareReplay(1)
      );
    }
    return this.optionsCache$;
  }

  private normalizeSchedulerOptionsPayload(payload: AutomatedSurveyOptions | null | undefined): AutomatedSurveyOptions {
    const empty = AutomatedSurveyService.emptyOptions();
    return {
      categories: payload?.categories ?? empty.categories,
      jointTypes: payload?.jointTypes ?? empty.jointTypes,
      methods: payload?.methods ?? empty.methods,
      statuses: payload?.statuses ?? empty.statuses,
      types: payload?.types ?? empty.types,
    };
  }

  /**
   * Automated-survey GET does not filter joints. Patient joint-laterality-options (with survey flags)
   * returns the joint list reception / automation should show.
   */
  private withPatientSurveyJointTypes(opts: AutomatedSurveyOptions): Observable<AutomatedSurveyOptions> {
    const tenantId = this.tenantContextService.getTenantId().trim();
    const base = this.patientApiUrl?.trim() ?? '';
    if (!tenantId || !base) {
      return of(opts);
    }
    const jointUrl = `${base}/v1/tenant/${tenantId}/patients/joint-laterality-options`;
    return this.http
      .get<IApiResponse<{ joints: { id: string; name: string; code?: string }[] }>>(jointUrl, {
        params: buildPatientSurveyJointLateralityParams(),
      })
      .pipe(
        map((jr) => {
          const joints = jr.data?.joints;
          if (!Array.isArray(joints)) {
            return opts;
          }
          return {
            ...opts,
            jointTypes: joints.map((j) => ({
              id: j.id,
              code: j.code ?? '',
              name: j.name,
              sortOrder: 0,
            })),
          };
        }),
        catchError(() => of(opts))
      );
  }

  private applyLoadedOptionsToSignals(data: AutomatedSurveyOptions): void {
    this.categoryRowsCache = data.categories ?? [];
    const jointTypes = [...(data.jointTypes ?? [])]
      .sort((a, b) => (a.name ?? '').localeCompare(b.name ?? '', undefined, { sensitivity: 'base' }))
      .map((item) => ({
        value: item.id || item.code,
        label: item.name ?? '',
        type: 'checkbox' as const,
      }));
    this.jointTypesSignal.set(jointTypes);
    this.deliveryMethodsSignal.set(
      (data.methods ?? []).map((item) => ({
        value: item.id,
        label: item.name ?? '',
        code: item.code,
        type: 'radio' as const,
      }))
    );
    this.statusesSignal.set(
      (data.statuses ?? []).map((item) => ({
        value: item,
        label: item,
        type: 'radio' as const,
      }))
    );
    this.surveyTypesSignal.set(
      [...(data.types ?? [])]
        .sort((a, b) => (a.name ?? '').localeCompare(b.name ?? '', undefined, { sensitivity: 'base' }))
        .map((item) => ({
          value: item.id || item.code,
          label: item.name ?? '',
          type: 'checkbox' as const,
        }))
    );
  }
  public getAppointmentTypes(): Observable<OptionInput[]> {
    // Appointment types are loaded via getAppointmentTypeOptions(surveyTypeId)
    return of(this.appointmentTypesSignal());
  }
  public getCategories(surveyType?: string | null): Observable<OptionInput[]> {
    if (this.categoryRowsCache.length > 0) {
      return of(this.getCategoriesSync(surveyType));
    }
    return this.loadOptions().pipe(map(() => this.getCategoriesSync(surveyType)));
  }
  public getJoints(): Observable<OptionInput[]> {
    if (this.jointTypesSignal().length > 0) {
      return of(this.jointTypesSignal());
    }
    return this.loadOptions().pipe(map(() => this.jointTypesSignal()));
  }
  public getDeliveryMethods(): Observable<OptionInput[]> {
    if (this.deliveryMethodsSignal().length > 0) {
      return of(this.deliveryMethodsSignal());
    }
    return this.loadOptions().pipe(map(() => this.deliveryMethodsSignal()));
  }
  public getStatuses(): Observable<OptionInput[]> {
    if (this.statusesSignal().length > 0) {
      return of(this.statusesSignal());
    }
    return this.loadOptions().pipe(map(() => this.statusesSignal()));
  }
  public getScheduleModes(): Observable<OptionInput[]> {
    if (this.scheduleModesSignal().length > 0) {
      return of(this.scheduleModesSignal());
    }
    return this.loadOptions().pipe(map(() => this.scheduleModesSignal()));
  }
  public getSurveyTypes(): Observable<OptionInput[]> {
    if (this.surveyTypesSignal().length > 0) {
      return of(this.surveyTypesSignal());
    }
    return this.loadOptions().pipe(map(() => this.surveyTypesSignal()));
  }
  public loadAllOptions(): Observable<AutomatedSurveyOptions> {
    return this.loadOptions();
  }
  public getAppointmentTypesSync(): OptionInput[] {
    return this.appointmentTypesSignal();
  }
  public getCategoriesSync(surveyType?: string | null): OptionInput[] {
    return this.mapCategories(this.categoryRowsCache, surveyType);
  }

  /** Resolves lk_appointment_category.code for a dropdown value (id or code). */
  public getCategoryCodeByValue(value: string): string | undefined {
    const normalized = value.trim();
    if (!normalized) {
      return undefined;
    }
    interface RawCategory {
      id?: string;
      code?: string;
    }
    const row = (this.categoryRowsCache as RawCategory[]).find(
      (item) => item.id === normalized || item.code === normalized
    );
    return row?.code?.trim() || undefined;
  }
  public getJointsSync(): OptionInput[] {
    return this.jointTypesSignal();
  }
  public getDeliveryMethodsSync(): OptionInput[] {
    return this.deliveryMethodsSignal();
  }
  public getStatusesSync(): OptionInput[] {
    return this.statusesSignal();
  }
  public getScheduleModesSync(): OptionInput[] {
    return this.scheduleModesSignal();
  }
  public getSurveyTypesSync(): OptionInput[] {
    return this.surveyTypesSignal();
  }
  public getDaysPriorOptions(): Observable<OptionInput[]> {
    const options: OptionInput[] = [
      { value: 0, label: 'Same Day', type: 'checkbox' },
      { value: 1, label: '1', type: 'checkbox' },
      { value: 2, label: '2', type: 'checkbox' },
      { value: 3, label: '3', type: 'checkbox' },
      { value: 4, label: '4', type: 'checkbox' },
      { value: 5, label: '5', type: 'checkbox' },
      { value: 6, label: '6', type: 'checkbox' },
      { value: 7, label: '7', type: 'checkbox' },
      { value: 10, label: '10', type: 'checkbox' },
      { value: 14, label: '14', type: 'checkbox' },
    ];
    return of<OptionInput[]>(options);
  }
}
